import { useRef } from 'react'
import { Combobox } from '@cutilei/ui'
import layoutOf from 'utils/layoutStyle'

/*
  Campo de escolha, sobre o Combobox do @cutilei/ui, com as props do wrapper de react-select de antes
  do Design System. As telas continuam passando e recebendo a opção `{ label, value }`, em que
  `value` pode ser uma entidade, um texto, um número, um booleano ou `null`:

  - `value` é a opção escolhida, ou o array delas no `isMulti`. Sem objeto (`null`, `''`, um valor
    solto), nada está escolhido, como no react-select;
  - `onChange(name, opção)` devolve o próprio objeto que veio em `options`. Limpar devolve `null`;
  - a escolhida que não está em `options`, como a da edição que monta o objeto, mostra o `label`
    dela;
  - `onBlur(name, true)` vem quando o foco sai do campo com a lista fechada, como o blur do
    react-select.

  No `isMulti`, a escolha nova vai para o fim do array. Desmarcar uma escolha na lista, ou tirar a
  última com Backspace, chama o `onRemoveOption(opção)`. Sem ele, ou no X que limpa tudo, vai para o
  `onChange` o array que sobrou. As escolhas que a tela tirou de `options` voltam para o fim da
  lista, para poderem ser desmarcadas.

  A opção de uma entidade com `profile_picture` mostra a foto, ou as iniciais sem ela.

  Com `error` e `touched`, o campo fica inválido, sem mensagem: a mensagem é da tela. Do
  `containerStyles`, só valem largura, margens e o encaixe no flex. O visual é o do Design System.

  A caixa é um botão: o Enter nela abre a lista, e não envia o formulário (ver `utils/submitOnEnter`).
  A lista abre num Popover, que funciona dentro do Dialog da lib. `onMenuOpen` e `onMenuClose` vêm
  quando ela abre e fecha, e `isOptionDisabled(opção)` bloqueia uma opção, como no react-select.

  A busca no servidor é do `SelectAsync`, que passa `onSearchChange`, `loading`, `loadingMessage`
  e `icon` direto ao Combobox. Com `onSearchChange`, o Combobox não filtra as `options`.
*/

const PREFIXES = { string: 's', number: 'n', boolean: 'b' }

/*
  A chave de uma opção, que o Combobox guarda como o valor dela. A entidade vale pelo `id`, porque a
  tela monta um objeto novo a cada render. O prefixo separa '', 0, false e null.
*/
function keyOf(value) {
  if (value === null || value === undefined) return String(value)
  if (typeof value === 'object') return value.id != null ? `id:${value.id}` : `json:${JSON.stringify(value)}`
  return `${PREFIXES[typeof value] ?? typeof value}:${value}`
}

const isOption = option => option !== null && typeof option === 'object' && !Array.isArray(option)

function toComboboxOption(key, option, isOptionDisabled) {
  const entity = isOption(option.value) ? option.value : null
  return {
    value: key,
    label: option.label == null ? '' : String(option.label),
    disabled: Boolean(option.isDisabled || isOptionDisabled?.(option)),
    ...(entity && 'profile_picture' in entity && { avatar: entity.profile_picture || null })
  }
}

export default function Select({
  name,
  value = '',
  touched,
  error,
  options,
  placeholder,
  isMulti = false,
  isClearable = false,
  isDisabled = false,
  onChange,
  onBlur = () => null,
  onRemoveOption,
  onMenuOpen,
  onMenuClose,
  isOptionDisabled,
  noOptionsMessage = 'Nenhuma opção encontrada',
  containerStyles,
  id,
  'aria-label': ariaLabel,
  onSearchChange,
  loading,
  loadingMessage,
  icon
}) {
  const open = useRef(false)

  // As opções, cada uma com a sua chave. Uma chave repetida ganha o índice, para não colidir. O
  // react-select só lia as `options` ao abrir o menu; aqui, o que não for array vira lista vazia, em
  // vez de derrubar a tela enquanto ela carrega.
  const counts = new Map()
  const entries = (Array.isArray(options) ? options : []).filter(isOption).map(option => {
    const base = keyOf(option.value)
    const count = counts.get(base) ?? 0
    counts.set(base, count + 1)
    return [count ? `${base}#${count}` : base, option]
  })
  const optionsByKey = new Map(entries)

  const chosen = isMulti
    ? (Array.isArray(value) ? value.filter(isOption) : [])
    : (isOption(value) ? [value] : [])
  const chosenByKey = new Map(chosen.map(option => [keyOf(option.value), option]))
  const missing = [...chosenByKey].filter(([key]) => !optionsByKey.has(key))

  const comboboxOptions = entries.map(([key, option]) => toComboboxOption(key, option, isOptionDisabled))
  const missingOptions = missing.map(([key, option]) => toComboboxOption(key, option))

  const handleSingleChange = key => {
    onChange(name, key === null ? null : (optionsByKey.get(key) ?? chosenByKey.get(key)))
  }

  const handleMultiChange = keys => {
    const removed = [...chosenByKey.keys()].filter(key => !keys.includes(key))
    if (removed.length === 1 && onRemoveOption) {
      onRemoveOption(chosenByKey.get(removed[0]))
      return
    }
    onChange(name, keys.map(key => chosenByKey.get(key) ?? optionsByKey.get(key)))
  }

  // O blur do campo é o da caixa, quando o foco vai para fora dele. Com a lista aberta, o foco
  // está na busca, num portal, e ainda é o mesmo campo.
  const handleBlur = event => {
    if (open.current || event.currentTarget.contains(event.relatedTarget)) return
    onBlur(name, true)
  }

  const common = {
    size: 'sm',
    placeholder,
    emptyMessage: noOptionsMessage,
    clearable: isClearable,
    disabled: isDisabled,
    error: error && touched ? true : undefined,
    id,
    // Com `id`, o nome vem da <label htmlFor> da tela. Sem ele, do placeholder.
    'aria-label': ariaLabel ?? (id ? undefined : placeholder),
    onSearchChange,
    loading,
    loadingMessage,
    icon,
    onOpenChange: next => {
      open.current = next
      if (next) onMenuOpen?.()
      else onMenuClose?.()
    }
  }

  /*
    A coluna `minmax(0, 1fr)` deixa o campo encolher numa grade ou num flex apertado, como o
    react-select encolhia, com o texto da escolha cortado. Sem ela, o texto escolhido seria a
    largura mínima do campo, e uma linha de campos estouraria o modal.
  */
  return (
    <div
      style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', width: '100%', ...layoutOf(containerStyles) }}
      onBlur={handleBlur}
    >
      {isMulti ? (
        <Combobox
          {...common}
          multiple
          options={[...comboboxOptions, ...missingOptions]}
          value={[...chosenByKey.keys()]}
          onValueChange={handleMultiChange}
        />
      ) : (
        <Combobox
          {...common}
          options={comboboxOptions}
          selectedOptions={missingOptions}
          value={chosen.length ? keyOf(chosen[0].value) : null}
          onValueChange={handleSingleChange}
        />
      )}
    </div>
  )
}
